<template>
  <BContainer
    v-if="user"
    class="mb-5"
    fluid
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BContainer
      fluid
    >
      <BRow>
        <BCol
          cols="12"
          md="8"
        >
          <BFormGroup
            label="Search"
            label-for="filterUserSearch"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <BFormInput
              id="filterUserSearch"
              v-model="tableUsers.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
          md="4"
        >
          <BFormGroup
            label="Role"
            label-for="filterRole"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <v-select
              v-model="tableUsers.selected.role"
              placeholder="search role here"
              input-id="filterRole"
              class="vs-white"
              label="name"
              clearable
              append-to-body
              :options="(tableUsers.options.roles)"
              :loading="(tableUsers.fetching.roles)"
              :disabled="(tableUsers.busy || tableUsers.fetching.roles)"
              :calculate-position="calculateDropPosition"
            >
              <template #option="{ name }">
                <div
                  class="d-flex py-1"
                  v-text="name"
                />
              </template>
              <template #no-options="">
                no available role
              </template>
            </v-select>
          </BFormGroup>
        </BCol>

        <BCol
          cols="12"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"
            @click.prevent="createUser"
          >
            Create User
          </BButton>
        </BCol>
      </BRow>

      <CustomTable
        ref="tableUsers"
        class="mt-3"
        :provider="tableProvider"
        :fields="tableUsers.fields"
        :filter="tableUsers.filter"
        :filter-on="tableUsers.filterOn"
        :sort-by="tableUsers.sortBy"
        :sort-desc="tableUsers.sortDesc"
        :sort-direction="tableUsers.sortDirection"
        :total-rows="tableUsers.totalRows"
        :current-page="tableUsers.currentPage"
        :per-page="tableUsers.perPage"
        :stacked="tableUsers.stacked"
        :busy="tableUsers.busy"
      >
        <template #cell(action)="{ item }">
          <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
            <BButton
              size="sm"
              variant="primary"
              :disabled="tableUsers.busy"
              @click.prevent="editUser(item)"
            >
              Edit
            </BButton>
          </div>
        </template>
      </CustomTable>
    </BContainer>

    <BModal
      ref="modalUser"
      scrollable
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      :title="ModalUserTitle"
      aria-hidden="false"
    >
      <ValidationObserver
        ref="formUser"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>

            <BCol
              cols="12"
            >
              <WVInput
                v-model="formUser.first_name"
                :disabled="state.busy"
                :validation-attr="{
                  name: 'first name',
                  vid: 'first_name',
                  rules: 'required|max:50'
                }"
                :form-group-attr="{
                  label: 'First Name',
                  labelClass: 'font-weight-bolder',
                  class: 'mb-2'
                }"
                placeholder="enter first name here"
                autocomplete="off"
                maxlength="50"
                type="text"
              />
            </BCol>

            <BCol
              cols="12"
            >
              <WVInput
                v-model="formUser.last_name"
                :disabled="state.busy"
                :validation-attr="{
                  name: 'last name',
                  vid: 'last_name',
                  rules: 'required|max:50'
                }"
                :form-group-attr="{
                  label: 'Last Name',
                  labelClass: 'font-weight-bolder',
                  class: 'mb-2'
                }"
                placeholder="enter last name here"
                autocomplete="off"
                maxlength="50"
                type="text"
              />
            </BCol>

            <BCol
              cols="12"
            >
              <WVInput
                v-model="formUser.notification_email"
                :disabled="state.busy"
                :validation-attr="{
                  name: 'notification email',
                  vid: 'notification_email',
                  rules: 'email|max:50'
                }"
                :form-group-attr="{
                  label: 'Notification Email',
                  labelClass: 'font-weight-bolder',
                  class: 'mb-2'
                }"
                placeholder="enter notification email here"
                autocomplete="off"
                maxlength="50"
                type="email"
              />
            </BCol>

            <BCol
              cols="12"
            >
              <WVInput
                v-model="formUser.username"
                :disabled="state.busy"
                :validation-attr="{
                  name: 'username',
                  vid: 'username',
                  rules: 'required|max:50'
                }"
                :form-group-attr="{
                  label: 'Username',
                  labelClass: 'font-weight-bolder',
                  class: 'mb-2'
                }"
                placeholder="enter username here"
                autocomplete="off"
                maxlength="50"
                type="text"
              />
            </BCol>

            <BCol
              cols="12"
            >
              <WVInput
                v-model="formUser.password"
                :disabled="state.busy"
                :validation-attr="{
                  name: 'password',
                  vid: 'password',
                  rules: vPassword
                }"
                :form-group-attr="{
                  label: 'Password',
                  labelClass: 'font-weight-bolder',
                  description: 'Leave blank when not changing password.',
                  class: 'mb-2'
                }"
                placeholder="enter password here"
                autocomplete="off"
                maxlength="50"
                type="password"
              />
            </BCol>

            <BCol
              cols="12"
            >
              <WVInput
                v-model="formUser.password_confirmation"
                :disabled="state.busy"
                :validation-attr="{
                  name: 'password confirmation',
                  vid: 'password_confirmation',
                  rules: vPasswordConfirmation
                }"
                :form-group-attr="{
                  label: 'Password Confirmation',
                  labelClass: 'font-weight-bolder',
                  description: 'Leave blank when not changing password.',
                  class: 'mb-2'
                }"
                no-password-toggle
                placeholder="enter password confirmation here"
                autocomplete="off"
                maxlength="50"
                type="password"
              />
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="role"
                vid="role"
                rules="required"
              >
                <BFormGroup
                  label="Role"
                  label-for="role"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <v-select
                    v-model="selected.role"
                    input-id="role"
                    placeholder="search role here"
                    label="name"
                    clearable
                    append-to-body
                    :options="(options.roles)"
                    :loading="(fetching.roles)"
                    :disabled="(state.busy || fetching.roles)"
                    :calculate-position="calculateDropPosition"
                  >
                    <template #option="{ name }">
                      <div
                        class="d-flex py-1"
                        v-text="name"
                      />
                    </template>
                    <template #no-options="">
                      no available role
                    </template>
                  </v-select>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="location"
                name="location"
                :rules="{
                  required: [1, 2].includes(Number(formUser.role))
                }"
              >
                <BFormGroup
                  label="Location"
                  label-for="location"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <v-select
                    v-model="selected.location"
                    input-id="location"
                    label="location_name"
                    clearable
                    append-to-body
                    placeholder="search location here"
                    :options="options.locations"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    :disabled="(state.busy || fetching.locations || [3, 6].includes(Number(formUser.role)))"
                    :loading="fetching.locations"
                  >
                    <template #option="{ active, allow_out, allow_in, location_code, location_name }">
                      <div class="d-flex flex-column py-1">
                        <div v-text="location_name" />
                        <div class="d-flex justify-content-between">
                          <strong v-text="location_code" />
                          <div>
                            <strong
                              class="text-primary mr-1"
                              v-text="(Number(allow_in) === 1 ? 'in' : '')"
                            />
                            <strong
                              class="text-primary mr-1"
                              v-text="(Number(allow_out) === 1 ? 'out' : '')"
                            />
                            <strong
                              :class="(Number(active) === 1 ? 'text-primary' : 'text-danger')"
                              v-text="(Number(active) === 1 ? 'active' : 'inactive')"
                            />
                          </div>
                        </div>
                      </div>
                    </template>
                    <template #no-options="">
                      no available location
                    </template>
                  </v-select>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup
                class="mb-2"
              >
                <BFormCheckbox
                  id="is_active"
                  v-model="formUser.active"
                  switch
                  value="1"
                  name="is_active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>

          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel, visible }">
        <BButton
          variant="primary"
          tabindex="-1"
          :disabled="state.busy"
          @click.prevent="validateUser"
        >
          {{ state.editing ? 'Update' : 'Create' }} User
        </BButton>
        <BButton
          v-if="visible"
          variant="outline-dark"
          tabindex="-1"
          :disabled="state.busy"
          @click.prevent="$event => {
            $event.target.blur()
            cancel()
          }"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import QUERY from 'lodash'
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import MLocations from '@/mixins/locations/locations'
import MRoles from '@/mixins/accounts/roles'

import { SAdminUser } from '@/services/admin'

export default {
  name: 'VAdminUsers',

  middleware: [
    'auth',
    'admin'
  ],

  mixins: [
    MISC,
    FORMATTER,
    MLocations,
    MRoles
  ],

  metaInfo: () => ({
    title: 'Users'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        password_visible: false
      },
      fetching: {
        roles: false,
        locations: false
      },
      options: {
        roles: [],
        locations: []
      },
      selected: {
        role: '',
        location: ''
      },
      formUser: {
        id: '',
        username: '',
        first_name: '',
        last_name: '',
        notification_email: '',
        password: '',
        password_confirmation: '',
        role: '',
        location: '',
        login_local: 0,
        active: 1
      },
      tableUsers: {
        busy: false,
        fetching: {
          roles: false
        },
        options: {
          roles: []
        },
        selected: {
          role: ''
        },
        filter: {
          search: '',
          role: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 9, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'created_at', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'username' },
          { mobile: 3, key: 'first_name' },
          { mobile: 4, key: 'last_name' },
          { mobile: 5, key: 'notification_email' },
          { mobile: 6, key: 'role.name', label: 'Role' },
          { mobile: 6, key: 'location.location_name', label: 'Location' },
          { mobile: 7, key: 'active', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 8, key: 'updated_at', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ModalUserTitle () {
      return this.state.editing ? 'Edit User' : 'Create User'
    },

    vPassword () {
      return `${this.state.editing ? '' : 'required|'}min:8`
    },

    vPasswordConfirmation () {
      return `${this.state.editing ? '' : 'required|'}confirmed:password`
    },

    ...mapGetters({
      user: 'auth/user'
    })
  },

  watch: {
    'selected.role' (role) {
      this.formUser.role = role?.id || ''

      if ([3, 4].includes(Number(role?.id))) {
        this.selected.location = ''
      }
    },

    'selected.location' (location) {
      this.formUser.location = location?.id || ''
    },

    'tableUsers.selected.role' (role) {
      this.tableUsers.filter.role = role?.id || ''
    }

  },

  mounted () {
    if (this.user) {
      this.getRoles(this.tableUsers)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableUsers.busy = true

      const filters = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        filter_role: filter.role
      }

      return await SAdminUser.get(filters).then(
        ({ data }) => {
          this.tableUsers.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableUsers.totalRows = 0
        return []
      }).finally(() => {
        this.tableUsers.busy = false
      })
    },

    createUser () {
      this.state.editing = false

      this.formUser = {
        id: '',
        username: '',
        first_name: '',
        last_name: '',
        notification_email: '',
        password: '',
        password_confirmation: '',
        active: 1
      }

      this.getRoles(this).finally(() => {
        this.getLocations(this)
      })

      this.$refs.modalUser.show()
    },

    editUser (user) {
      this.state.editing = true

      this.formUser = Object.assign(this.formUser, {
        id: user.id,
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        notification_email: user.notification_email,
        password: '',
        password_confirmation: '',
        active: user.active
      })

      this.getRoles(this).finally(() => {
        this.selected.role = QUERY.find(this.options.roles, {
          id: Number(user.role_id)
        })
      })

      this.getLocations(this).finally(() => {
        this.selected.location = QUERY.find(this.options.locations, {
          id: Number(user.location_id)
        }) || ''
      })

      this.$refs.modalUser.show()
    },

    async validateUser () {
      await this.$refs.formUser.validate().then(
        async validUser => {
          if (!validUser) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this User?</h6>' : '<h6>Create this User?</h6>',
            preConfirm: () => {
              this.state.busy = true
              const createOrUpdate = () => {
                return this.state.editing ? this.putUser() : this.postUser()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formUser.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postUser () {
      return await SAdminUser.post(this.formUser).then(
        ({ data: { message } }) => {
          this.$refs.modalUser.hide()

          this.swalSuccess(message).then(() => {
            this.$refs.tableUsers.refetch()
          })
        }
      )
    },

    async putUser () {
      return await SAdminUser.put(this.formUser).then(
        ({ data: { message, user } }) => {
          this.$refs.modalUser.hide()

          this.swalSuccess(message).then(() => {
            this.$refs.tableUsers.updateRow({
              id: user.id,
              username: user.username,
              first_name: user.first_name,
              last_name: user.last_name,
              notification_email: user.notification_email,
              role: user.role,
              role_id: user.role_id,
              location: user.location,
              location_id: user.location_id,
              active: user.active,
              updated_at: user.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
